import type {
    TeamType,
    RoundBetMethod,
    RoundState,
    Participant,
    Location as LocationGQL,
    Member as MemberGQL,
    Team as TeamGQL,
    InvitationSetting,
    Invitation,
    MembershipPolicy,
} from "../generated";
import type {Member} from "./member";
import type {GameType} from "@atg-tillsammans-shared/types";

export type TeamRound = {
    betMethod: RoundBetMethod | null;
    description: string | null;
    game: unknown | null;
    gameId: string;
    gameInitiator: MemberGQL | null;
    id: string;
    isGameInitiator: boolean;
    sales: {
        buyLimit: number | null;
        buyLimitPerPerson: number | null;
        deadline: string | null;
        id: string;
        price: number | null;
        refundAmount: number | null;
        scheduledReleases: Array<{
            id: string;
            numberOfShares: number;
            time: string;
        }> | null;
        totalSalesAmount: number | null;
    };
    salesDefaults: {
        buyLimitPerPerson: number;
        price: number;
    } | null;
    state: RoundState;
    teamId: string;
};

export enum TeamRoundInfoStatus {
    ACTIVE = "ACTIVE",
    ACTIVE_WITH_PARTICIPATION = "ACTIVE_WITH_PARTICIPATION",
    INACTIVE = "INACTIVE",
}

export enum TeamSettingsUpdateMethod {
    REMOVE_TEAM = "REMOVE_TEAM",
    LEAVE_TEAM = "LEAVE_TEAM",
}

export enum TeamSettingsErrorCode {
    ACCESS_DENIED = "ACCESS_DENIED",
    NOT_MEMBER_OF_TEAM = "NOT_MEMBER_OF_TEAM",
    LACKS_ROLE = "LACKS_ROLE",
    UPDATE_TEAM_DB_ERROR = "UPDATE_TEAM_DB_ERROR",
    TEAM_NAME_ALREADY_EXISTS = "TEAM_NAME_ALREADY_EXISTS",
    TEAM_ALREADY_EXISTS = "TEAM_ALREADY_EXISTS",
    MAX_NR_OF_TEAMS = "MAX_NR_OF_TEAMS",
    GAME_TYPE_LIST_IS_EMPTY = "GAME_TYPE_LIST_IS_EMPTY",
    ACTIVE_ROUND_OF_GAME_TYPE_EXISTS = "ACTIVE_ROUND_OF_GAME_TYPE_EXISTS",
    ACTIVE_ROUND_EXISTS = "ACTIVE_ROUND_EXISTS",
    MEMBERS_EXISTS = "MEMBERS_EXISTS",
    IS_CAPTAIN = "IS_CAPTAIN",
    IS_GAME_INITIATOR = "IS_GAME_INITIATOR",
    IS_PARTICIPATING = "IS_PARTICIPATING",
}

export enum TeamSettingsSuccessCode {
    DEFAULT = "DEFAULT",
    CLOSE_TEAM = "CLOSE_TEAM",
    LEAVE_TEAM = "LEAVE_TEAM",
}

export enum PreBookedErrorCode {
    RGS_NETLOSS_LIMIT_EXCEEDED = "RGS_NETLOSS_LIMIT_EXCEEDED",
    RGS_NETLOSS_SERVER_ERROR = "RGS_NETLOSS_SERVER_ERROR",
    RGS_CASINO_LOSS_LIMIT_NOT_SET = "RGS_CASINO_LOSS_LIMIT_NOT_SET",
    RGS_CASINO_HIGH_LOSS_LIMIT = "RGS_CASINO_HIGH_LOSS_LIMIT",
    RGS_CASINO_LOSS_LIMIT_EXCEEDED = "RGS_CASINO_LOSS_LIMIT_EXCEEDED",
    SUBSCRIPTION_NOT_FOUND = "SUBSCRIPTION_NOT_FOUND",
    ACCESS_DENIED = "ACCESS_DENIED",
    NOT_MEMBER_OF_TEAM = "NOT_MEMBER_OF_TEAM",
    RGS_SELF_EXCLUSION = "RGS_SELF_EXCLUSION",
    RGS_TEMPORARY_SELF_EXCLUSION = "RGS_TEMPORARY_SELF_EXCLUSION",
    RGS_COVID_SELF_EXCLUSION = "RGS_COVID_SELF_EXCLUSION",
    RGS_NAR_EXCLUSION = "RGS_NAR_EXCLUSION",
    RGS_NAR_UNDETERMINED = "RGS_NAR_UNDETERMINED",
    RGS_DEPOSIT_LIMIT_NOT_SET = "RGS_DEPOSIT_LIMIT_NOT_SET",
    RGS_HIGH_DEPOSIT_LIMIT = "RGS_HIGH_DEPOSIT_LIMIT",
    RGS_KYC_QUESTIONNAIRE_NOT_SUBMITTED = "RGS_KYC_QUESTIONNAIRE_NOT_SUBMITTED",
    RGS_CASINO_PLAY_TIME_LIMIT_EXCEEDED = "RGS_CASINO_PLAY_TIME_LIMIT_EXCEEDED",
    RGS_CASINO_PLAY_TIME_LIMIT_NOT_SET = "RGS_CASINO_PLAY_TIME_LIMIT_NOT_SET",
    RGS_CASINO_FULL_PLAY_LIMIT_NOT_SET = "RGS_CASINO_FULL_PLAY_LIMIT_NOT_SET",
    RGS_CALL_FAILED = "RGS_CALL_FAILED",
    GENERAL_ERROR = "GENERAL_ERROR",
}

export enum TeamMembersErrorCode {
    TEAM_IS_FULL = "TEAM_IS_FULL",
    TOO_MANY_TEAMS = "TOO_MANY_TEAMS",
    GENERAL_ERROR = "GENERAL_ERROR",
}

export type TeamID = string;

export interface Location {
    postalCode?: string;
    postalTown?: string;
}

export interface Team extends Omit<TeamGQL, "location" | "captain"> {
    // Team basics
    id: TeamID;
    name: string;
    captain: MemberGQL; // Member;
    type: TeamType;
    gameTypes: GameType[];
    teamImageUrl: string | null;
    description: string | null;

    // Team info
    ranking: number;
    // location?: Location;
    location: LocationGQL | null;

    // Team settings
    locationVisible: boolean;
    membershipPolicy: MembershipPolicy;
    invitationSetting: InvitationSetting;
    batchBetAllowed: boolean;
    multipleReleasesAllowed: boolean;
    systemsLimit: number | null;

    // Team roles
    isTeamCaptain: boolean;

    // Team membership
    /**
     * List of members in the team. Mostly intended for STANDARD teams.
     */
    members?: Member[];
    /**
     * Number of vacant memberships in the team. Only available for STANDARD teams.
     */
    vacantMemberships: number;
    /**
     * Indicates if the current user is a member of the team.
     */
    isTeamMember?: boolean;

    // Team restrictions

    // Team meta data
    createdDate: string;
    visible: boolean;

    // Team invitation info
    invitation: Invitation | null;

    // Team rounds and participations (Maybe should be thier own type?)
    rounds?: TeamRound[];
    participations?: Participant[];
}
